import { ArrowDropDown } from '@mui/icons-material';
import { Grid } from '@mui/material';
import { CustomFields, ReportTask } from '@nx-smartmonkey/shared/domain';
import { HSpacer, RoutalPalette, VSpacer } from '@nx-smartmonkey/ui';
import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { storageService } from '../../../../../../../../../application/StorageService';
import img2of5 from '../../../../../../../../../assets/img/barcodes/2of5.png';
import codabarImg from '../../../../../../../../../assets/img/barcodes/codabar.png';
import code128Img from '../../../../../../../../../assets/img/barcodes/code_128.png';
import code39Img from '../../../../../../../../../assets/img/barcodes/code_39.png';
import code39VinImg from '../../../../../../../../../assets/img/barcodes/code_39_vin.png';
import code93Img from '../../../../../../../../../assets/img/barcodes/code_93.png';
import eanImg from '../../../../../../../../../assets/img/barcodes/ean.png';
import ean8Img from '../../../../../../../../../assets/img/barcodes/ean_8.png';
import i2of5Img from '../../../../../../../../../assets/img/barcodes/i2of5.png';
import qrImg from '../../../../../../../../../assets/img/barcodes/qr.png';
import upcImg from '../../../../../../../../../assets/img/barcodes/upc.png';
import upcEImg from '../../../../../../../../../assets/img/barcodes/upc_e.png';
import iconCloseCamera from '../../../../../../../../../assets/img/camera/icon-closeCamera.svg';
import { LoaderPage } from '../../../../../../../../../components/LoaderPage';
import { STOP_TASK_STATUS, StopTaskStatus } from '../../../../../../../../../constants/Stops';
import { StorageKeys } from '../../../../../../../../../constants/StorageKeys';
import { Stop } from '../../../../../../../../../domain/stop/Stop';
import { StopTask } from '../../../../../../../../../domain/stop/StopTask';
import { RouteStopTaskField } from '../../../../../../../../../domain/stop/StopTaskField';
import { useDriversState } from '../../../../../../../../../hooks/useDriversState';
import { ScanCodeReadersTypes, ScanDeviceTypes } from '../../../../../../../../../interfaces/Scanner';
import Button from '../../../../../../../../../react-components/Button';
import { Text } from '../../../../../../../../../react-components/Text';
import { useRetrieveDriverCredentials } from '../../../../../../../hooks/useRetrieveDriverCredentials';
import { useRetrieveVehicle } from '../../../../../../../hooks/useRetrieveVehicle';
import { TaskCustomFields } from '../../tasks/TaskCustomFields';
import { CodesScanner } from './CodesScanner';

const readers: Array<{ key: ScanCodeReadersTypes; image: React.ReactElement }> = [
  {
    key: `ean_reader`,
    image: <img src={eanImg} width="70%" style={{ mixBlendMode: `multiply` }} alt="" />,
  },
  {
    key: `ean_8_reader`,
    image: <img src={ean8Img} width="70%" style={{ mixBlendMode: `multiply` }} alt="" />,
  },
  {
    key: `code_39_reader`,
    image: <img src={code39Img} width="70%" style={{ mixBlendMode: `multiply` }} alt="" />,
  },
  {
    key: `code_39_vin_reader`,
    image: <img src={code39VinImg} width="70%" style={{ mixBlendMode: `multiply` }} alt="" />,
  },
  {
    key: `code_93_reader`,
    image: <img src={code93Img} width="70%" style={{ mixBlendMode: `multiply` }} alt="" />,
  },
  {
    key: `code_128_reader`,
    image: <img src={code128Img} width="70%" style={{ mixBlendMode: `multiply` }} alt="" />,
  },
  {
    key: `codabar_reader`,
    image: <img src={codabarImg} width="70%" style={{ mixBlendMode: `multiply` }} alt="" />,
  },
  {
    key: `upc_reader`,
    image: <img src={upcImg} width="70%" style={{ mixBlendMode: `multiply` }} alt="" />,
  },
  {
    key: `upc_e_reader`,
    image: <img src={upcEImg} width="70%" style={{ mixBlendMode: `multiply` }} alt="" />,
  },
  {
    key: `i2of5_reader`,
    image: <img src={i2of5Img} width="70%" style={{ mixBlendMode: `multiply` }} alt="" />,
  },
  {
    key: `2of5_reader`,
    image: <img src={img2of5} width="70%" style={{ mixBlendMode: `multiply` }} alt="" />,
  },
  {
    key: `qr_reader`,
    image: <img src={qrImg} width="70%" style={{ mixBlendMode: `multiply` }} alt="" />,
  },
];

interface StopReportScannerProps {
  onClose: () => void;
  onUpdateTasks: (task: StopTask, status: StopTaskStatus, taskValues?: CustomFields) => void;
  selectedStopTasks: StopTask[];
  selectedStop: Stop;
  reportingTasks: Record<string, ReportTask>;
  totalCodes: number;
  scannedCodes: number;
}

const SCAN_CODE_STATUS = {
  idle: `idle`,
  scanOk: `scan_ok`,
  scanError: `scan_error`,
};

type ScannerMode = `scan_tasks` | `create_tasks`;

export const ScanCodeStatus = [SCAN_CODE_STATUS.idle, SCAN_CODE_STATUS.scanOk, SCAN_CODE_STATUS.scanError] as const;

export type ScanCodeStatusTypes = (typeof ScanCodeStatus)[number];

export const StopReportScanner = ({
  onClose,
  onUpdateTasks,
  selectedStopTasks,
  selectedStop,
  reportingTasks,
  totalCodes,
  scannedCodes,
}: StopReportScannerProps) => {
  const { data: credentials } = useRetrieveDriverCredentials();
  const { data: vehicleData } = useRetrieveVehicle();
  const { driversState } = useDriversState();

  const scannerRef = useRef(null);
  const [status, setStatus] = useState<ScanCodeStatusTypes>(`idle`);
  const [selectTypeShow, setSelectTypeShow] = useState<boolean>(false);
  const [showStopTasks, setShowStopTasks] = useState<boolean>(false);
  const [taskFound, setTaskFound] = useState<StopTask | null>(null);
  const [scannerMode, setScannerMode] = useState<ScannerMode>(`scan_tasks`);
  const [tempTasks, setTempTasks] = useState<StopTask[]>([]);
  const [tempReports, setTempReports] = useState<Record<string, ReportTask>>({});
  const intl = useIntl();

  const [scanType, setScanType] = useState<ScanCodeReadersTypes | undefined>(undefined);
  const [scanDevice, setScanDevice] = useState<ScanDeviceTypes>(`scanner`);
  const fetchStorageData = async () => {
    const resultTypeScan = await storageService.getItem({ key: StorageKeys.DRIVERS_SCAN_CODE_READER });
    if (resultTypeScan) {
      setScanType(resultTypeScan as ScanCodeReadersTypes);
    } else {
      setSelectTypeShow(true);
    }
    const resultScan = await storageService.getItem({ key: StorageKeys.DRIVERS_SCANNING_DEVICE });
    if (resultScan) {
      setScanDevice(resultScan as ScanDeviceTypes);
    } else {
      setScanDevice(`camera`);
    }
  };

  useEffect(() => {
    fetchStorageData();
  }, []);

  // //TODO REFACTOR. Esto es un parche para re-renderizar la camara y se muestre, si hay tiempo investigar y arreglar
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const [isScanneRefOn, setIsScanneRefOn] = useState(false);

  useEffect(() => {
    if (scannerRef.current) {
      setIsScanneRefOn(true);
    }
  }, [scannerRef]);
  // FIN DEL PARCHE

  useEffect(() => {
    if (!credentials?.test(`can_update_stop_tasks`) && totalCodes === scannedCodes) {
      onClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scannedCodes]);

  useEffect(() => {
    if (status === SCAN_CODE_STATUS.scanOk) {
      setTimeout(() => {
        if (!credentials?.test(`can_update_stop_tasks`) && totalCodes === scannedCodes) {
          onClose();
        } else {
          setStatus(SCAN_CODE_STATUS.idle);
        }
      }, 1400);
    } else if (status === SCAN_CODE_STATUS.scanError) {
      setTimeout(() => {
        setStatus(SCAN_CODE_STATUS.idle);
      }, 2000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const validateCode = (code: string) => {
    if (status !== SCAN_CODE_STATUS.scanOk) {
      // We look for the tasks that are pending and have the barcode code that we detected
      const taskFound = selectedStopTasks.find((task) => {
        if (reportingTasks[task.id].status !== `pending`) {
          setTaskFound(null);
          return false;
        } else {
          if (task.fields.some((field) => field.id === `barcode` && field.value === code)) setTaskFound(task);
          return task.fields.some((field) => field.id === `barcode` && field.value === code);
        }
      });

      if (taskFound) {
        setStatus(SCAN_CODE_STATUS.scanOk);
        if (vehicleData?.project.fields.task_completed_fields.some((field) => field.is_required))
          setShowStopTasks(true);
        else onUpdateTasks(taskFound, STOP_TASK_STATUS.completed as StopTaskStatus);
      } else if (scannerMode === `create_tasks` && credentials?.test(`can_update_stop_tasks`)) {
        //  In case the task hasn't been found, and the user is in create_task mode, it will create it

        const tempTaskFound = tempTasks.find((task) => task.fields.some((field) => field.value === code));

        if (tempTaskFound) {
          if (tempReports[tempTaskFound.id].status === `pending`) {
            setTaskFound(tempTaskFound);
            if (vehicleData?.project.fields.task_completed_fields.some((field) => field.is_required))
              setShowStopTasks(true);
            else onUpdateTasks(tempTaskFound, STOP_TASK_STATUS.completed as StopTaskStatus);
            setStatus(SCAN_CODE_STATUS.scanOk);
          } else {
            setStatus(SCAN_CODE_STATUS.scanError);
          }
          return;
        }

        const newTask = StopTask.create(
          {
            stop_id: selectedStop.id,
            label: code,
            status: `pending`,
            fields: [
              RouteStopTaskField.create({
                id: `barcode`,
                label: code,
                type: `barcode`,
                value: code,
                is_custom_field: false,
              }),
            ],

            // CRUD
            created_at: new Date(),
          },
          code // We are asignign the code as the ID, because it shouldn't be repeated in any other task
        );
        setTempReports({
          ...tempReports,
          [newTask.id]: ReportTask.create({
            task_id: newTask.id,
            status: `pending`,
          }),
        });
        setTempTasks([...tempTasks, newTask]);
        setTaskFound(newTask);
        if (vehicleData?.project.fields.task_completed_fields.some((field) => field.is_required))
          setShowStopTasks(true);
        else onUpdateTasks(newTask, STOP_TASK_STATUS.completed as StopTaskStatus);
      } else setStatus(SCAN_CODE_STATUS.scanError);
    }
  };

  const updateTask = (task: StopTask, status: StopTaskStatus, taskValues?: CustomFields) => {
    const tempTask = tempTasks.find((t) => t.id === task.id);

    if (tempTask) {
      tempTask.status = status;
      setTempTasks([...tempTasks.filter((t) => t.id !== tempTask.id), tempTask]);
      const newTempReports = tempTasks.reduce((prev, curr) => {
        if (tempReports[curr.id]) {
          return {
            ...prev,
            [curr.id]: tempReports[curr.id],
          };
        }
        return {
          ...prev,
          [curr.id]: ReportTask.create({
            task_id: curr.id,
            status: status,
            custom_fields: taskValues,
          }),
        };
      }, {});
      setTempReports(newTempReports);
      onUpdateTasks(tempTask, status, taskValues);
    } else {
      onUpdateTasks(task, status, taskValues);
    }
  };

  if (showStopTasks && taskFound && vehicleData?.project) {
    const isTemp = tempTasks.find((task) => task.id === taskFound.id);
    return (
      <div
        style={{
          position: `absolute`,
          width: `100%`,
          height: `100%`,
          inset: 0,
          zIndex: 1005,
          backgroundColor: RoutalPalette.primary60,
        }}
      >
        <TaskCustomFields
          customFields={vehicleData.project.fields.task_completed_fields}
          task={taskFound}
          reportTask={isTemp ? tempReports[taskFound.id] : reportingTasks[taskFound.id]}
          status={taskFound.status}
          onClose={() => {
            updateTask(taskFound, STOP_TASK_STATUS.pending as StopTaskStatus);
            setTaskFound(null);
            onClose();
          }}
          onConfirm={(taskValues: CustomFields) => {
            updateTask(taskFound, STOP_TASK_STATUS.completed as StopTaskStatus, taskValues);
            setTaskFound(null);
          }}
        />
      </div>
    );
  }

  return (
    <>
      {!isVideoLoaded && isScanneRefOn && (
        <>
          <div
            style={{
              position: `absolute`,
              width: `100%`,
              height: `100%`,
              inset: 0,
              zIndex: 1004,
              backgroundColor: RoutalPalette.primary60,
            }}
          >
            {LoaderPage({})}
          </div>
          <Grid
            container
            wrap="nowrap"
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            style={{
              top: 0,
              left: 0,
              right: 0,
              zIndex: 1004,
              position: `absolute`,
              padding: `20px`,
              width: `100%`,
              height: `fit-content`,
            }}
          >
            <Grid item container direction="column" style={{ width: `80%`, height: `64px` }}></Grid>

            <Grid item style={{ display: `flex`, justifyContent: `center`, alignItems: `center` }}>
              <img
                src={iconCloseCamera}
                alt="close"
                onClick={() => {
                  onClose();
                }}
                style={{ fontSize: `28px`, height: `50px`, width: `50px` }}
              />
            </Grid>
          </Grid>
        </>
      )}
      <div
        style={{
          position: `absolute`,
          width: `100%`,
          height: `100%`,
          inset: 0,
          zIndex: 1004,
          backgroundColor: RoutalPalette.primary20,
        }}
      >
        {/* SCANNER TYPE SELECTOR */}
        {selectTypeShow ? (
          <Grid
            container
            wrap="nowrap"
            direction="column"
            style={{
              position: `absolute`,
              backgroundColor: RoutalPalette.white,
              zIndex: 2,
              height: `100%`,
              overflowY: `auto`,
              padding: `20px`,
            }}
          >
            <Grid
              item
              style={{ display: `flex`, justifyContent: `space-between`, alignItems: `center`, marginBottom: `8px` }}
            >
              <Grid>
                <Text
                  intlId="driver.stop.report.tasks.scanner.select_reader"
                  style={{ color: RoutalPalette.grey1, fontSize: `24px` }}
                />
              </Grid>

              <img
                src={iconCloseCamera}
                alt="close"
                onClick={() => {
                  if (!scanType) {
                    setScanType(readers[0].key);
                    storageService.setItem({ key: StorageKeys.DRIVERS_SCAN_CODE_READER, item: readers[0].key });
                  }
                  setSelectTypeShow(false);
                }}
                style={{ fontSize: `28px` }}
              />
            </Grid>
            <VSpacer medium />

            {readers.map((reader, idx: number) => {
              return (
                <Grid
                  key={reader.key}
                  item
                  container
                  wrap="nowrap"
                  direction="column"
                  onClick={() => {
                    setScanType(reader.key);
                    storageService.setItem({ key: StorageKeys.DRIVERS_SCAN_CODE_READER, item: reader.key });
                    setSelectTypeShow(false);
                  }}
                  style={{
                    cursor: `pointer`,
                    padding: `8px`,
                    border: `${scanType === reader.key ? `none` : `1px solid ${RoutalPalette.primary40}`}`,
                    backgroundColor: `${scanType === reader.key ? RoutalPalette.grey4 : `transparent`}`,
                    borderRadius: `10px`,
                    ...(idx !== 0 ? { marginTop: `10px` } : {}),
                  }}
                >
                  <Grid item>
                    <Text
                      intlId={`driver.stop.report.tasks.scanner.${reader.key}`}
                      style={{ color: RoutalPalette.grey1, fontSize: `27px` }}
                    />
                  </Grid>
                  <Grid item container justifyContent="center" style={{ marginBottom: `10px` }}>
                    {reader.image}
                  </Grid>
                </Grid>
              );
            })}
          </Grid>
        ) : null}

        {/* SCANNER CONTAINER */}
        <div ref={scannerRef} style={{ position: `relative`, width: `100%`, height: `100%` }}>
          {/* HEADER */}
          <Grid
            container
            direction="column"
            style={{
              top: 0,
              left: 0,
              right: 0,
              zIndex: 1,
              position: scanDevice === `camera` ? `absolute` : undefined, // this is made so that when the mode is on scanner, the input is easily aligned with the header
              padding: `20px 20px 16px 20px`,
              width: `100%`,
              height: `auto`,
            }}
          >
            <Grid container wrap="nowrap" direction="row" alignItems="center" justifyContent="space-between">
              <Grid item container direction="column" style={{ width: `80%`, minHeight: 0 }}>
                <Grid item container direction="row">
                  <Button
                    onClick={() => setSelectTypeShow(!selectTypeShow)}
                    style={{
                      backgroundColor: RoutalPalette.white,
                      border: `1px solid ${RoutalPalette.grey1}`,
                      width: `100%`,
                      display: `flex`,
                      justifyContent: `space-between`,
                      margin: 0,
                    }}
                  >
                    <Text
                      tagName="span"
                      intlId={`driver.stop.report.tasks.scanner.${scanType}`}
                      style={{ color: RoutalPalette.text, marginTop: `2px` }}
                    />
                    <ArrowDropDown style={{ fill: RoutalPalette.text }} />
                  </Button>
                </Grid>
              </Grid>

              <Grid item style={{ display: `flex`, justifyContent: `center`, alignItems: `center` }}>
                <img
                  src={iconCloseCamera}
                  alt="close"
                  onClick={() => {
                    onClose();
                  }}
                  style={{ fontSize: `28px`, height: `40px`, width: `40px`, marginRight: `5px` }}
                />
              </Grid>
            </Grid>

            {credentials?.test(`can_update_stop_tasks`) && (
              <>
                <VSpacer medium />
                <Grid
                  container
                  wrap="nowrap"
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  style={{
                    backgroundColor: `${RoutalPalette.primary20n}80`, // This is made to make it with opacity 50% without affecting the children
                    borderRadius: `10px`,
                    width: `100%`,
                  }}
                >
                  <Button
                    variant={scannerMode === `scan_tasks` ? `default` : `flat`}
                    intlId="driver.stop.report.tasks.scanner.scan"
                    style={{
                      borderRadius: `10px`,
                      margin: `5px`,
                      padding: 0,
                      display: `flex`,
                      width: `50%`,
                    }}
                    onClick={() => {
                      setScannerMode(`scan_tasks`);
                    }}
                  />
                  <Button
                    variant={scannerMode === `create_tasks` ? `default` : `flat`}
                    intlId="driver.stop.report.tasks.scanner.create"
                    style={{
                      borderRadius: `10px`,
                      margin: `5px`,
                      padding: 0,
                      display: `flex`,
                      width: `50%`,
                    }}
                    onClick={() => {
                      setScannerMode(`create_tasks`);
                    }}
                  />
                </Grid>
              </>
            )}
          </Grid>

          {/* IF THE USER USES THE CAMERA */}
          {scanDevice === `camera` && (
            <>
              <video
                style={{
                  width: `100%`,
                  height: `100%`,
                  objectFit: `cover`,
                }}
              />

              <Grid
                style={{
                  position: `absolute`,
                  width: driversState.width,
                  height: driversState.height,
                }}
              >
                <CodesScanner
                  scannerRef={scannerRef}
                  constraints={
                    {
                      // width: driversState.width,
                      // height: driversState.height,
                    }
                  }
                  onDetected={(code: string) => validateCode(code)}
                  decoders={[scanType!]}
                  locate
                  onScannerReady={() => {
                    setIsVideoLoaded(true);
                  }}
                />
              </Grid>
              <div
                style={{
                  // backgroundColor: `yellow`,
                  position: `absolute`,
                  width: (driversState.width * 80) / 100,
                  height: `234px`,
                  left: `calc(50% - 40% - 3px)`,
                  top: `calc(50% - 120px)`,
                  border: `3px ${
                    status === SCAN_CODE_STATUS.scanError
                      ? `solid ${RoutalPalette.error.medium}`
                      : status === SCAN_CODE_STATUS.scanOk
                      ? `solid ${RoutalPalette.success.medium}`
                      : `dashed ${RoutalPalette.white}`
                  }`,
                }}
              />

              {status === SCAN_CODE_STATUS.scanError && (
                <div
                  style={{
                    position: `absolute`,
                    width: `80%`,
                    height: `40px`,
                    marginTop: `10px`,
                    backgroundColor: RoutalPalette.warning.medium,
                    left: `10%`,
                    top: `calc(50% + 120px)`,
                    textAlign: `center`,
                    display: `flex`,
                    flexDirection: `row`,
                    justifyContent: `center`,
                    alignItems: `center`,
                    borderRadius: `10px`,
                  }}
                >
                  <Text
                    intlId="driver.stop.report.tasks.scanner.error"
                    style={{ color: RoutalPalette.secondary40, fontSize: `16px`, fontWeight: `bold` }}
                  />
                </div>
              )}

              {/* FOOTER HINT */}
              <Grid
                style={{
                  display: `flex`,
                  flexDirection: `column`,
                  position: `absolute`,
                  width: `calc(100%)`,
                  bottom: 0,
                  textAlign: `center`,
                  padding: `20px`,
                }}
              >
                <Grid
                  display={`flex`}
                  flexDirection={`row`}
                  style={{
                    backgroundColor: `${RoutalPalette.neutral40}80`,
                    padding: `16px 10px`,
                    justifyContent: `center`,
                    alignItems: `center`,
                    borderRadius: `10px`,
                    height: `54px`,
                  }}
                >
                  <Text
                    variant="body-large"
                    intlId="driver.stop.report.tasks.scanner.codes_scanned"
                    style={{ color: RoutalPalette.white }}
                  />
                  <HSpacer small />
                  <Text
                    variant="body-large"
                    style={{ color: RoutalPalette.white }}
                  >{`${scannedCodes}/${totalCodes}`}</Text>
                </Grid>

                <VSpacer small />
                {credentials?.test(`can_update_stop_tasks`) && (
                  <Grid
                    display={`flex`}
                    flexDirection={`row`}
                    style={{
                      backgroundColor: `${RoutalPalette.neutral40}80`,
                      padding: `16px 10px`,
                      justifyContent: `center`,
                      alignItems: `center`,
                      borderRadius: `10px`,
                      height: `54px`,
                    }}
                  >
                    <Text
                      variant="body-large"
                      intlId="driver.stop.report.tasks.scanner.codes_created"
                      style={{ color: RoutalPalette.white }}
                    />
                    <HSpacer small />
                    <Text variant="body-large" style={{ color: RoutalPalette.white }}>{`${tempTasks.length}`}</Text>
                  </Grid>
                )}
              </Grid>
            </>
          )}

          {/* IF THE USER USES A SCANNER DEVICE */}
          {scanDevice === `scanner` && (
            <>
              <Grid style={{ width: `100%`, padding: `0 20px 0 20px` }}>
                <input
                  id="scannerInput"
                  onBlur={(event) => {
                    // This prevents the input from not being focused
                    event.target.focus();
                  }}
                  onKeyDown={(event) => {
                    if (event.key === `Enter`) {
                      const input = document.getElementById(`scannerInput`) as HTMLInputElement;
                      if (input.value) {
                        validateCode(input.value.replaceAll(` `, ``));
                        input.value = ``;
                      }
                    }
                  }}
                  onChange={(event) => {
                    if (event.target.value.length > 0) {
                      const value = event.target.value.replaceAll(` `, ``);
                      event.target.value = value;
                    }
                  }}
                  autoFocus
                  style={{
                    borderRadius: `10px`,
                    padding: `5px`,
                    height: `1,25rem`,
                    width: `100%`,
                    boxSizing: `border-box`,
                    border: `3px ${
                      status === SCAN_CODE_STATUS.scanError
                        ? `solid ${RoutalPalette.error.medium}`
                        : status === SCAN_CODE_STATUS.scanOk
                        ? `solid ${RoutalPalette.success.medium}`
                        : `dashed ${RoutalPalette.white}`
                    }`,
                  }}
                  placeholder={intl.formatMessage({
                    id:
                      scannerMode === `scan_tasks`
                        ? `driver.stop.report.tasks.scanner.scanner_device_info.scan`
                        : `driver.stop.report.tasks.scanner.scanner_device_info.create`,
                    defaultMessage: `Scan to interact`,
                  })}
                />
              </Grid>
              {/* FOOTER HINT */}
              <Grid
                style={{
                  display: `flex`,
                  flexDirection: `column`,
                  width: `calc(100%)`,
                  textAlign: `center`,
                  padding: `16px 20px 20px 20px`,
                }}
              >
                <Grid
                  display={`flex`}
                  flexDirection={`row`}
                  style={{
                    backgroundColor: `${RoutalPalette.neutral40}80`,
                    padding: `12px 8px`,
                    justifyContent: `center`,
                    alignItems: `center`,
                    borderRadius: `10px`,
                  }}
                >
                  <Text
                    intlId="driver.stop.report.tasks.scanner.codes_scanned"
                    style={{ color: RoutalPalette.white, fontSize: `16px` }}
                  />
                  <HSpacer small />
                  <Text
                    style={{ fontSize: `16px`, color: RoutalPalette.white }}
                  >{`${scannedCodes}/${totalCodes}`}</Text>
                </Grid>

                <VSpacer small />

                {credentials?.test(`can_update_stop_tasks`) && (
                  <Grid
                    display={`flex`}
                    flexDirection={`row`}
                    style={{
                      backgroundColor: `${RoutalPalette.neutral40}80`,
                      padding: `12px 8px`,
                      justifyContent: `center`,
                      alignItems: `center`,
                      borderRadius: `10px`,
                    }}
                  >
                    <Text
                      intlId="driver.stop.report.tasks.scanner.codes_created"
                      style={{ color: RoutalPalette.white, fontSize: `16px` }}
                    />
                    <HSpacer small />
                    <Text style={{ fontSize: `16px`, color: RoutalPalette.white }}>{`${tempTasks.length}`}</Text>
                  </Grid>
                )}

                {status !== SCAN_CODE_STATUS.idle && (
                  <Grid
                    style={{
                      backgroundColor: `${
                        status === SCAN_CODE_STATUS.scanError
                          ? RoutalPalette.error.medium
                          : RoutalPalette.success.medium
                      }80`,
                      padding: `12px 8px`,
                      justifyContent: `center`,
                      alignItems: `center`,
                      borderRadius: `10px`,
                      marginTop: `20px`,
                    }}
                  >
                    <Text
                      intlId={
                        status === SCAN_CODE_STATUS.scanError
                          ? `driver.stop.report.tasks.scanner.error`
                          : `driver.stop.report.tasks.scanner.success`
                      }
                      style={{ color: RoutalPalette.white, fontSize: `16px` }}
                    />
                  </Grid>
                )}
              </Grid>
            </>
          )}
        </div>
      </div>
    </>
  );
};
